import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { twoFaApi } from '../services/api'
import toast from 'react-hot-toast'

// Google Authenticator non espone uno schema URL pubblico per essere
// semplicemente "aperto" (a differenza del flusso di setup, che usa
// otpauth:// per aggiungere un nuovo account): su Android si può comunque
// lanciare l'app installata via intent link, con fallback automatico al
// Play Store se non è installata; su iOS/desktop non c'è alternativa
// migliore che aprire lo store.
const PACKAGE_ANDROID = 'com.google.android.apps.authenticator2'
const APP_STORE_IOS = 'https://apps.apple.com/app/google-authenticator/id388497605'

function apriGoogleAuthenticator() {
  const ua = navigator.userAgent
  if (/Android/i.test(ua)) {
    const fallback = encodeURIComponent(`https://play.google.com/store/apps/details?id=${PACKAGE_ANDROID}`)
    window.location.href = `intent://#Intent;package=${PACKAGE_ANDROID};S.browser_fallback_url=${fallback};end`
  } else {
    window.open(APP_STORE_IOS, '_blank')
  }
}

function ParticleCanvas() {
  const ref = useRef<HTMLCanvasElement>(null)
  useEffect(() => {
    const c = ref.current; if (!c) return
    const ctx = c.getContext('2d')!
    let id: number
    const resize = () => { c.width = innerWidth; c.height = innerHeight }
    resize(); window.addEventListener('resize', resize)
    const N = 35
    const p = Array.from({length:N},()=>({
      x:Math.random()*innerWidth, y:Math.random()*innerHeight,
      vx:(Math.random()-.5)*.3, vy:(Math.random()-.5)*.3,
      r:Math.random()*1.5+.8
    }))
    const draw = () => {
      ctx.clearRect(0,0,c.width,c.height)
      for(let i=0;i<N;i++) for(let j=i+1;j<N;j++){
        const dx=p[i].x-p[j].x, dy=p[i].y-p[j].y
        const d=Math.sqrt(dx*dx+dy*dy)
        if(d<110){
          ctx.beginPath()
          ctx.strokeStyle=`rgba(37,99,235,${.09*(1-d/110)})`
          ctx.lineWidth=1
          ctx.moveTo(p[i].x,p[i].y)
          ctx.lineTo(p[j].x,p[j].y)
          ctx.stroke()
        }
      }
      p.forEach(pt=>{
        ctx.beginPath(); ctx.arc(pt.x,pt.y,pt.r,0,Math.PI*2)
        ctx.fillStyle='rgba(37,99,235,.18)'; ctx.fill()
        pt.x+=pt.vx; pt.y+=pt.vy
        if(pt.x<0||pt.x>c.width) pt.vx*=-1
        if(pt.y<0||pt.y>c.height) pt.vy*=-1
      })
      id=requestAnimationFrame(draw)
    }
    draw()
    return ()=>{ window.removeEventListener('resize',resize); cancelAnimationFrame(id) }
  },[])
  return <canvas ref={ref} style={{position:'fixed',inset:0,zIndex:0,pointerEvents:'none'}}/>
}

export default function Login() {
  const { login, loginWithToken } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const redirectTo = (location.state as { from?: { pathname: string; search: string } } | null)?.from
  const redirectPath = redirectTo ? `${redirectTo.pathname}${redirectTo.search || ''}` : '/'
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [showReset, setShowReset] = useState(false)
  const [resetEmail, setResetEmail] = useState('')
  const [resetSent, setResetSent] = useState(false)
  const [resetLoading, setResetLoading] = useState(false)
  const [totpStep, setTotpStep] = useState(false)
  const [partialToken, setPartialToken] = useState('')
  const [totpCode, setTotpCode] = useState('')
  const [totpLoading, setTotpLoading] = useState(false)
  const [totpResetLoading, setTotpResetLoading] = useState(false)

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault(); setLoading(true); setError('')
    try {
      const res = await login(email, password)
      if (res.requires_2fa && res.partial_token) {
        setPartialToken(res.partial_token)
        setTotpStep(true)
      } else {
        navigate(redirectPath)
      }
    }
    catch (err: any) {
      const detail = err?.response?.data?.detail
      setError(typeof detail === 'string' ? detail : 'Email o password non corretti.')
    }
    finally { setLoading(false) }
  }

  const handleTotp = async (e: React.FormEvent) => {
    e.preventDefault(); setTotpLoading(true); setError('')
    try {
      const res = await twoFaApi.verifyLogin(partialToken, totpCode)
      loginWithToken({ ...res.data, email })
      navigate(redirectPath)
    } catch {
      setError('Codice non valido o scaduto.')
    } finally {
      setTotpLoading(false)
    }
  }

  const handleTotpResetRichiesta = async () => {
    setTotpResetLoading(true)
    try {
      const res = await twoFaApi.richiediResetTotp(partialToken)
      toast.success(res.data?.message || "Richiesta inviata all'amministratore.")
    } catch {
      toast.error('Errore invio richiesta, riprova più tardi.')
    } finally {
      setTotpResetLoading(false)
    }
  }

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault(); setResetLoading(true)
    try {
      await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resetEmail })
      })
    } catch {}
    setResetSent(true); setResetLoading(false)
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=DM+Sans:wght@400;500;600&display=swap');

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html, body { min-height: 100%; overflow-x: hidden; overflow-y: auto; }
        body {
          font-family: 'DM Sans', sans-serif;
          background: linear-gradient(135deg, #f8fafc, #eef2ff, #faf5ff);
        }

        .lp-grid {
          position: fixed; inset: 0; z-index: 0; pointer-events: none;
          background-image:
            linear-gradient(rgba(0,0,0,0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0,0,0,0.05) 1px, transparent 1px);
          background-size: 40px 40px;
        }

        .lp-page {
          position: relative; z-index: 1;
          min-height: 100vh;
          display: flex; flex-direction: column;
          align-items: center; justify-content: center;
          padding: 32px 16px 64px;
        }

        .lp-row {
          display: flex; flex-direction: row;
          align-items: center; justify-content: center;
          gap: 56px; width: 100%; max-width: 1060px;
        }

        .lp-left {
          flex: 1 1 300px; max-width: 500px;
        }

        .lp-card {
          width: 100%; max-width: 400px; flex-shrink: 0;
          background: #ffffff; border-radius: 24px;
          padding: 40px 36px;
          box-shadow: 0 20px 60px rgba(0,0,0,.10), 0 0 0 1px rgba(0,0,0,.05);
        }

        .lp-h1 {
          font-family: 'Syne', sans-serif;
          font-size: clamp(28px, 3.5vw, 48px);
          font-weight: 800; line-height: 1.18;
          color: #1e293b; letter-spacing: -.03em;
          margin-bottom: 16px;
        }

        .sophia-word {
          background: linear-gradient(90deg, #f97316, #7c3aed);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          font-weight: 900; display: inline-block;
          animation: sophiaGlow 3s ease-in-out infinite alternate;
        }

        .lp-card-title {
          font-family: 'Syne', sans-serif;
          font-size: 22px; font-weight: 800;
          color: #0f172a; letter-spacing: -.02em;
          margin-bottom: 6px;
        }
        .lp-card-sub { font-size: 14px; color: #94a3b8; margin-bottom: 26px; }

        .lp-label {
          display: block; font-size: 11px; font-weight: 600;
          color: #475569; letter-spacing: .07em;
          text-transform: uppercase; margin-bottom: 7px;
        }

        .lp-input {
          display: block; width: 100%;
          padding: 14px 16px;
          font-size: 16px;
          font-family: 'DM Sans', sans-serif;
          color: #0f172a; background: #f8fafc;
          border: 1.5px solid #e2e8f0; border-radius: 12px;
          outline: none; -webkit-appearance: none; appearance: none;
          transition: border-color .2s, box-shadow .2s;
        }
        .lp-input:focus {
          border-color: #2563eb;
          box-shadow: 0 0 0 3px rgba(37,99,235,.12);
          background: #fff;
        }

        .lp-field { margin-bottom: 16px; }

        .lp-btn {
          display: block; width: 100%; padding: 15px; min-height: 52px;
          border: none; border-radius: 14px;
          background: linear-gradient(90deg, #2563eb, #7c3aed);
          color: #fff; font-family: 'Syne', sans-serif;
          font-size: 16px; font-weight: 700; cursor: pointer;
          transition: transform .2s, box-shadow .2s; margin-top: 6px;
        }
        .lp-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 12px 24px rgba(124,58,237,.28);
        }
        .lp-btn:disabled { background: #94a3b8; cursor: not-allowed; }

        .lp-forgot {
          display: block; width: 100%; background: none; border: none;
          color: #2563eb; font-size: 14px;
          font-family: 'DM Sans', sans-serif;
          cursor: pointer; padding: 10px 0; min-height: 44px;
          text-align: center; margin-top: 4px;
        }

        .lp-error {
          background: #fef2f2; border: 1px solid #fecaca;
          border-radius: 10px; padding: 11px 14px;
          font-size: 13px; color: #b91c1c; margin-bottom: 12px;
        }

        .lp-status {
          display: flex; align-items: center; justify-content: center;
          gap: 7px; margin-top: 24px; padding-top: 18px;
          border-top: 1px solid #f1f5f9;
          font-size: 12px; color: #94a3b8;
        }
        .lp-dot {
          width: 7px; height: 7px; border-radius: 50%;
          background: #22c55e; box-shadow: 0 0 6px #22c55e;
          flex-shrink: 0;
        }

        .lp-badges { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 24px; }
        .lp-badge {
          background: #fff; padding: 7px 14px; border-radius: 20px;
          font-size: 13px; color: #334155; font-weight: 500;
          box-shadow: 0 4px 14px rgba(0,0,0,.07);
          opacity: 0; animation: fadeUp .5s forwards;
        }

        .lp-footer {
          position: fixed; bottom: 0; left: 0; right: 0;
          text-align: center; padding: 10px 16px;
          font-size: 12px; color: rgba(100,116,139,.75);
          z-index: 10; pointer-events: none;
          background: linear-gradient(to top, rgba(248,250,252,.9), transparent);
        }
        .lp-footer strong { color: rgba(37,99,235,.75); }

        .lp-logo-mobile { display: none; margin-bottom: 20px; }

        /* ── MOBILE ── */
        @media (max-width: 680px) {
          .lp-left       { display: none !important; }
          .lp-row        { flex-direction: column; gap: 0; }
          .lp-card       { max-width: 100%; padding: 32px 24px; border-radius: 20px; }
          .lp-logo-mobile { display: block; }
        }

        /* Modal */
        .lp-overlay {
          position: fixed; inset: 0; z-index: 50;
          background: rgba(0,0,0,.55); backdrop-filter: blur(6px);
          display: flex; align-items: center; justify-content: center;
          padding: 20px 16px;
        }
        .lp-modal {
          background: #fff; border-radius: 20px;
          padding: 36px 32px; width: 100%; max-width: 360px;
          box-shadow: 0 24px 60px rgba(0,0,0,.15);
          animation: scaleIn .3s cubic-bezier(.22,1,.36,1);
        }
        .lp-modal-title {
          font-family: 'Syne', sans-serif;
          font-size: 20px; font-weight: 800;
          color: #0f172a; margin-bottom: 8px; letter-spacing: -.02em;
        }
        .lp-modal-sub { font-size: 14px; color: #64748b; margin-bottom: 20px; }

        @keyframes sophiaGlow {
          from { filter: drop-shadow(0 0 6px rgba(249,115,22,.4)); }
          to   { filter: drop-shadow(0 0 18px rgba(124,58,237,.5)); }
        }
        @keyframes scaleIn {
          from { transform: scale(.92); opacity: 0; }
          to   { transform: scale(1); opacity: 1; }
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin { to { transform: rotate(360deg); } }

        .lp-spinner {
          display: inline-block; width: 17px; height: 17px;
          border: 2px solid rgba(255,255,255,.35);
          border-top-color: #fff; border-radius: 50%;
          animation: spin .7s linear infinite;
          vertical-align: middle; margin-right: 8px;
        }
      `}</style>

      <div className="lp-grid" />
      <ParticleCanvas />

      <div className="lp-page">
        <div className="lp-row">

          {/* Colonna sinistra — solo desktop */}
          <div className="lp-left">
            <div style={{marginBottom:28}}>
              <img src="/logo-xsophia.png" alt="X-Sophia 360"
                style={{height:50,width:'auto',objectFit:'contain',display:'block'}}
                onError={e=>{(e.currentTarget as HTMLImageElement).style.display='none'}}/>
            </div>
            <h1 className="lp-h1">
              Field operations?{' '}
              <span style={{color:'#2563eb',fontWeight:700}}>Don't worry,</span>
              <br/>everything is under<br/>your control with{' '}
              <span className="sophia-word">Sophia</span>.
            </h1>
            <p style={{fontSize:15,color:'#64748b',lineHeight:1.65,maxWidth:420}}>
              Il sistema di gestione operativa sul campo per X-Sophia 360.
              Pratiche, tecnici, rapportini e fatturazione in un'unica piattaforma.
            </p>
            <div className="lp-badges">
              {['✦ AI-powered','Secure','Real-time Control'].map((b,i)=>(
                <span key={b} className="lp-badge"
                  style={{animationDelay:`${.6+i*.15}s`}}>{b}</span>
              ))}
            </div>
          </div>

          {/* Card login */}
          <div className="lp-card">

            {totpStep ? (
              <>
                {/* Logo — visibile solo su mobile */}
                <div className="lp-logo-mobile">
                  <img src="/logo-xsophia.png" alt="X-Sophia 360"
                    style={{height:40,width:'auto',objectFit:'contain',display:'block'}}
                    onError={e=>{(e.currentTarget as HTMLImageElement).style.display='none'}}/>
                </div>
                <div className="lp-card-title">Verifica 2FA</div>
                <div className="lp-card-sub">Inserisci il codice a 6 cifre dall'app autenticatore</div>
                <form onSubmit={handleTotp}>
                  <div className="lp-field">
                    <label className="lp-label">Codice TOTP</label>
                    <input
                      className="lp-input"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]{6}"
                      maxLength={6}
                      placeholder="000000"
                      value={totpCode}
                      onChange={e => setTotpCode(e.target.value.replace(/\D/g, ''))}
                      autoFocus
                      required
                      style={{letterSpacing:'0.3em', fontSize:22, textAlign:'center'}}
                    />
                  </div>
                  {error && <div className="lp-error">{error}</div>}
                  <button type="submit" className="lp-btn" disabled={totpLoading || totpCode.length !== 6}>
                    {totpLoading ? <><span className="lp-spinner"/>Verifica...</> : 'Conferma'}
                  </button>
                  <button type="button" className="lp-forgot" onClick={apriGoogleAuthenticator}>
                    Apri Google Authenticator
                  </button>
                  <button type="button" className="lp-forgot" disabled={totpResetLoading}
                    onClick={handleTotpResetRichiesta}>
                    {totpResetLoading ? 'Invio richiesta...' : 'Ho perso l\'app authenticator'}
                  </button>
                  <button type="button" className="lp-forgot"
                    onClick={() => { setTotpStep(false); setTotpCode(''); setError('') }}>
                    Torna al login
                  </button>
                </form>
              </>
            ) : (
              <>
            <img src="/logo-xsophia.png" alt="X-Sophia 360"
              style={{width:'100%',maxWidth:320,height:'auto',objectFit:'contain',display:'block',marginBottom:10}}
              onError={e=>{(e.currentTarget as HTMLImageElement).style.display='none'}}/>
            <div className="lp-card-sub">Inserisci le tue credenziali per continuare</div>

            <form onSubmit={handleLogin}>
              <div className="lp-field">
                <label htmlFor="lp-email" className="lp-label">Email</label>
                <input
                  id="lp-email"
                  className="lp-input"
                  type="email"
                  placeholder="nome@azienda.it"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  inputMode="email"
                />
              </div>

              <div className="lp-field">
                <label htmlFor="lp-password" className="lp-label">Password</label>
                <input
                  id="lp-password"
                  className="lp-input"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
              </div>

              {error && <div className="lp-error">{error}</div>}

              <button type="submit" className="lp-btn" disabled={loading}>
                {loading
                  ? <><span className="lp-spinner"/>Accesso in corso...</>
                  : 'Accedi'}
              </button>

              <button type="button" className="lp-forgot"
                onClick={() => setShowReset(true)}>
                Ho dimenticato la password
              </button>

            </form>

            <div className="lp-status">
              <span className="lp-dot"/>
              Sistema operativo · X-Sophia 360
            </div>
              </>
            )}
          </div>

        </div>
      </div>

      {/* Footer */}
      <div className="lp-footer">
        Created with precision by <strong>Sborchia Communications</strong>
      </div>

      {/* Modal reset password */}
      {showReset && (
        <div className="lp-overlay"
          onClick={e => {
            if (e.target === e.currentTarget) {
              setShowReset(false); setResetSent(false); setResetEmail('')
            }
          }}>
          <div className="lp-modal">
            {!resetSent ? (
              <>
                <div className="lp-modal-title">Reset password</div>
                <div className="lp-modal-sub">
                  Inserisci la tua email. Riceverai una password temporanea.
                </div>
                <form onSubmit={handleReset}
                  style={{display:'flex',flexDirection:'column',gap:12}}>
                  <input
                    className="lp-input"
                    type="email"
                    placeholder="nome@azienda.it"
                    value={resetEmail}
                    onChange={e => setResetEmail(e.target.value)}
                    required
                    autoComplete="email"
                    inputMode="email"
                  />
                  <button type="submit" className="lp-btn" disabled={resetLoading}>
                    {resetLoading ? 'Invio...' : 'Invia password temporanea'}
                  </button>
                  <button type="button" className="lp-forgot"
                    onClick={() => setShowReset(false)}>
                    Annulla
                  </button>
                </form>
              </>
            ) : (
              <div style={{textAlign:'center'}}>
                <div style={{
                  width:56,height:56,borderRadius:'50%',background:'#f0fdf4',
                  margin:'0 auto 16px',display:'flex',
                  alignItems:'center',justifyContent:'center'
                }}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
                    stroke="#16a34a" strokeWidth="2.5"
                    strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                </div>
                <div className="lp-modal-title">Email inviata</div>
                <div className="lp-modal-sub" style={{marginBottom:24}}>
                  Se l'indirizzo è registrato riceverai la password temporanea
                  entro pochi minuti.
                </div>
                <button className="lp-btn"
                  onClick={() => {
                    setShowReset(false); setResetSent(false); setResetEmail('')
                  }}>
                  Chiudi
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}
